import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Trash2, X } from 'lucide-react'
import type { Taak, TaakPrioriteit, TaakStatus } from '../../../gedeeld/types'
import { api } from '../../lib/api'
import { geleden, PRIORITEITEN, prioriteit, sleutelVan, STATUSSEN } from '../../lib/opmaak'
import { meld, meldFout, openTaak, taakGewijzigd, useBasis } from '../../lib/staat'
import { Kies, KiesMens, Knop, Leeg, vraagBevestiging } from '../ui'
import { Deadline, Labels, PrioriteitIcoon, SoortIcoon, StatusPil } from '../taak/stukjes'
import type { ProjectContext } from '../ProjectScherm'

/* ===========================================================================
 *  De lijst
 *
 *  Voor wie het bord te los vindt: alles in rijen, te sorteren op elke
 *  kolom, te groeperen, en met meerdere tegelijk aan te passen. Vink tien
 *  taken aan en zet ze in één keer in de sprint of op iemands naam -- dat is
 *  het verschil tussen een middag plannen en een kwartier.
 * =========================================================================== */

type Sorteer = 'nummer' | 'titel' | 'status' | 'wie' | 'prioriteit' | 'punten' | 'deadline' | 'bijgewerkt'
type Groep = 'geen' | 'status' | 'wie' | 'sprint' | 'epic'

const STATUS_ORDE: Record<TaakStatus, number> = { bezig: 0, wacht: 1, te_doen: 2, klaar: 3 }

export default function Lijst({ ctx }: { ctx: ProjectContext }) {
  const { projecten, mensen } = useBasis()
  const [sorteer, setSorteer] = useState<Sorteer>('status')
  const [omhoog, setOmhoog] = useState(true)
  const [groep, setGroep] = useState<Groep>('geen')
  const [gekozen, setGekozen] = useState<Set<string>>(new Set())

  const gesorteerd = useMemo(() => {
    const v = (t: Taak): string | number => {
      switch (sorteer) {
        case 'nummer': return t.nummer ?? 0
        case 'titel': return t.titel.toLowerCase()
        case 'status': return STATUS_ORDE[t.status]
        case 'wie': return (t.toegewezenNaam ?? '~').toLowerCase()
        case 'prioriteit': return -prioriteit(t.prioriteit).gewicht
        case 'punten': return t.punten ?? -1
        case 'deadline': return t.deadline ?? Number.MAX_SAFE_INTEGER
        case 'bijgewerkt': return -t.updatedAt
      }
    }
    return [...ctx.zicht].sort((a, b) => {
      const x = v(a), y = v(b)
      const r = x < y ? -1 : x > y ? 1 : (a.nummer ?? 0) - (b.nummer ?? 0)
      return omhoog ? r : -r
    })
  }, [ctx.zicht, sorteer, omhoog])

  const groepen = useMemo(() => {
    if (groep === 'geen') return [{ key: 'alles', naam: '', taken: gesorteerd }]
    const m = new Map<string, Taak[]>()
    const sleutel = (t: Taak) => groep === 'status' ? t.status : groep === 'wie' ? (t.toegewezenAan ?? '-')
      : groep === 'sprint' ? (t.sprintId ?? '-') : (t.soort === 'epic' ? t.id : t.ouderId ?? '-')
    for (const t of gesorteerd) m.set(sleutel(t), [...(m.get(sleutel(t)) ?? []), t])
    const naam = (k: string) => {
      if (k === '-') return groep === 'wie' ? 'Niemand' : groep === 'sprint' ? 'Backlog' : 'Zonder epic'
      if (groep === 'status') return STATUSSEN.find((s) => s.key === k)?.label ?? k
      if (groep === 'wie') return mensen.find((x) => x.id === k)?.naam ?? 'Onbekend'
      if (groep === 'sprint') return ctx.sprints.find((s) => s.id === k)?.naam ?? 'Sprint'
      return ctx.taken.find((t) => t.id === k)?.titel ?? 'Epic'
    }
    return [...m.entries()].map(([k, taken]) => ({ key: k, naam: naam(k), taken }))
  }, [gesorteerd, groep, mensen, ctx.sprints, ctx.taken])

  function kop(s: Sorteer, label: string) {
    const actief = sorteer === s
    return (
      <button type="button" className={`kolomkop ${actief ? 'actief' : ''}`}
        onClick={() => { if (actief) setOmhoog((x) => !x); else { setSorteer(s); setOmhoog(true) } }}>
        {label}{actief && (omhoog ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </button>
    )
  }

  async function zet(id: string, deel: Partial<Taak>) {
    const voor = ctx.taken
    ctx.setTaken((l) => l.map((t) => (t.id === id ? { ...t, ...deel } : t)))
    try {
      taakGewijzigd(await api.taakOpslaan({ id, ...deel }))
    } catch (e) {
      ctx.setTaken(() => voor)
      meldFout(e)
    }
  }

  async function voorAlle(deel: Partial<Taak>, wat: string) {
    const ids = [...gekozen]
    let goed = 0
    for (const id of ids) {
      try {
        taakGewijzigd(await api.taakOpslaan({ id, ...deel }))
        goed++
      } catch (e) { meldFout(e); break }
    }
    if (goed) meld(`${goed} ${goed === 1 ? 'taak' : 'taken'} ${wat}`)
    setGekozen(new Set())
  }

  async function allesWeg() {
    const ids = [...gekozen]
    if (!(await vraagBevestiging(`${ids.length} ${ids.length === 1 ? 'taak' : 'taken'} weggooien? Dat kan niet ongedaan worden gemaakt.`, 'Weggooien'))) return
    for (const id of ids) {
      try {
        await api.taakVerwijderen(id)
        taakGewijzigd({ id, weg: true })
      } catch (e) { meldFout(e); break }
    }
    setGekozen(new Set())
  }

  const alleGekozen = ctx.zicht.length > 0 && ctx.zicht.every((t) => gekozen.has(t.id))

  if (!ctx.taken.length) {
    return <Leeg titel="Nog geen taken in dit project" tekst="Druk op C, of op Taak rechtsboven." />
  }

  return (
    <div className="lijst-wrap">
      <div className="werkbalk dun">
        <span className="stil">Groeperen</span>
        <Kies<Groep> klein waarde={groep} onKies={setGroep} opties={[
          { key: 'geen', label: 'Niet' },
          { key: 'status', label: 'Op status' },
          { key: 'wie', label: 'Op persoon' },
          { key: 'sprint', label: 'Op sprint' },
          { key: 'epic', label: 'Op epic' },
        ]} />
      </div>

      {gekozen.size > 0 && (
        <div className="bulkbalk">
          <strong>{gekozen.size} gekozen</strong>
          <Kies<TaakStatus> klein waarde={null} leeg="Status…" onKies={(status) => void voorAlle({ status }, `op ${STATUSSEN.find((s) => s.key === status)?.label.toLowerCase()} gezet`)}
            opties={STATUSSEN.map((s) => ({ key: s.key, label: s.label, kleur: s.kleur }))} />
          <KiesMens klein waarde={null} mensen={mensen} leegTekst="Toewijzen…"
            onKies={(m) => void voorAlle({ toegewezenAan: m?.id ?? null }, m ? `aan ${m.naam} gegeven` : 'losgemaakt')} />
          <Kies<TaakPrioriteit> klein waarde={null} leeg="Prioriteit…" onKies={(p) => void voorAlle({ prioriteit: p }, 'bijgewerkt')}
            opties={PRIORITEITEN.map((p) => ({ key: p.key, label: p.label }))} />
          <Kies<string> klein waarde={null} leeg="Sprint…" onKies={(s) => void voorAlle({ sprintId: s || null }, s ? 'in de sprint gezet' : 'naar de backlog')}
            opties={[{ key: '', label: 'Backlog' }, ...ctx.sprints.filter((s) => s.status !== 'afgerond').map((s) => ({ key: s.id, label: s.naam }))]} />
          <span className="vul" />
          <Knop klein soort="gevaar" icoon={<Trash2 size={14} />} onClick={() => void allesWeg()}>Weggooien</Knop>
          <Knop klein soort="stil" icoon={<X size={14} />} onClick={() => setGekozen(new Set())} />
        </div>
      )}

      <div className="tabel" role="table">
        <div className="tabel-kop" role="row">
          <span><input type="checkbox" checked={alleGekozen} aria-label="Alles kiezen"
            onChange={() => setGekozen(alleGekozen ? new Set() : new Set(ctx.zicht.map((t) => t.id)))} /></span>
          {kop('nummer', 'Nr')}
          {kop('titel', 'Titel')}
          {kop('status', 'Status')}
          {kop('wie', 'Wie')}
          {kop('prioriteit', 'Prio')}
          {kop('punten', 'Pnt')}
          <span className="kolomkop">Sprint</span>
          {kop('deadline', 'Deadline')}
          {kop('bijgewerkt', 'Bijgewerkt')}
        </div>
        {groepen.map((g) => (
          <div key={g.key} role="rowgroup">
            {g.naam && <div className="tabel-groep">{g.naam}<span className="stil">{g.taken.length}</span></div>}
            {g.taken.map((t) => (
              <div key={t.id} role="row" className={`tabel-regel ${gekozen.has(t.id) ? 'gekozen' : ''} ${t.status === 'klaar' ? 'af' : ''}`}>
                <span><input type="checkbox" checked={gekozen.has(t.id)} aria-label="Kiezen"
                  onChange={() => setGekozen((x) => { const n = new Set(x); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n })} /></span>
                <span className="mono stil">{sleutelVan(t, projecten)}</span>
                <button type="button" className="tabel-titel" onClick={() => openTaak(t.id)}>
                  <SoortIcoon soort={t.soort} grootte={13} />
                  <span>{t.titel}</span>
                  <Labels namen={t.labels} project={ctx.project} />
                </button>
                <span>
                  <Kies<TaakStatus> klein waarde={t.status} onKies={(status) => void zet(t.id, { status })}
                    opties={STATUSSEN.map((s) => ({ key: s.key, label: s.label, kleur: s.kleur }))}
                    render={() => <StatusPil status={t.status} />} />
                </span>
                <span><KiesMens klein waarde={t.toegewezenAan} mensen={mensen} onKies={(m) => void zet(t.id, { toegewezenAan: m?.id ?? null, toegewezenNaam: m?.naam ?? null })} /></span>
                <span>
                  <Kies<TaakPrioriteit> klein waarde={t.prioriteit} onKies={(p) => void zet(t.id, { prioriteit: p })}
                    opties={PRIORITEITEN.map((p) => ({ key: p.key, label: p.label, icoon: <PrioriteitIcoon p={p.key} /> }))}
                    render={() => <PrioriteitIcoon p={t.prioriteit} />} />
                </span>
                <span className="getal">{t.punten ?? ''}</span>
                <span className="stil afkap">{ctx.sprints.find((s) => s.id === t.sprintId)?.naam ?? ''}</span>
                <span><Deadline taak={t} kort /></span>
                <span className="stil">{geleden(t.updatedAt)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
